import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../config/api";
import { useAuth } from "../AuthContext";

const tabs = [
  { id: "bounce", label: "Missed Return Cycles" },
  { id: "risk", label: "At Risk" },
  { id: "lost", label: "Lost" },
  { id: "target", label: "Bounce-Back List" },
  { id: "monthly", label: "Monthly Customers" },
  { id: "new-returning", label: "New vs Returning" },
  { id: "frequency", label: "Frequency" },
  { id: "high-value", label: "High-Value Missing" },
  { id: "service-retention", label: "Service Retention" },
  { id: "campaign", label: "Campaign Export" },
];

const segmentTabs = new Set(["bounce", "risk", "lost", "target", "campaign"]);
const periodTabs = new Set(["monthly", "new-returning", "frequency", "service-retention"]);

function currentMonthStart() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
}

function todayString() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatMoney(value) {
  return `AED ${Number(value || 0).toFixed(2)}`;
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(2)}%`;
}

function formatServices(services) {
  if (!services || services.length === 0) return "";
  return services.slice(0, 3).join(", ");
}

const cell = { padding: 6, borderBottom: "1px solid var(--table-border)" };
const rightCell = { ...cell, textAlign: "right" };
const headCell = { ...cell, textAlign: "left", fontWeight: "bold" };
const rightHeadCell = { ...headCell, textAlign: "right" };

function Table({ columns, rows, emptyText = "No data found." }) {
  if (!rows || rows.length === 0) return <div>{emptyText}</div>;

  return (
    <div style={{ overflow: "auto", border: "1px solid var(--table-border)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                style={column.align === "right" ? rightHeadCell : headCell}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.customer_id || row.service_id || row.month || row.bucket || index}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  style={column.align === "right" ? rightCell : cell}
                >
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const customerColumn = { key: "name", label: "Customer" };
const phoneColumn = { key: "phone", label: "Phone" };
const lastBillColumn = { key: "last_bill_date", label: "Last Bill" };
const daysColumn = { key: "days_since_last_visit", label: "Days", align: "right" };
const missedCyclesColumn = { key: "bounce_count", label: "Missed Cycles", align: "right" };
const spendColumn = { key: "total_spend", label: "Spend", align: "right", render: (row) => formatMoney(row.total_spend) };
const servicesColumn = { key: "last_services", label: "Last Services", render: (row) => formatServices(row.last_services) };

const customerReportColumns = {
  bounce: [
    customerColumn,
    phoneColumn,
    lastBillColumn,
    daysColumn,
    missedCyclesColumn,
    servicesColumn,
  ],
  risk: [
    customerColumn,
    phoneColumn,
    lastBillColumn,
    daysColumn,
    missedCyclesColumn,
    spendColumn,
    servicesColumn,
  ],
  lost: [
    customerColumn,
    phoneColumn,
    lastBillColumn,
    daysColumn,
    spendColumn,
    servicesColumn,
  ],
  target: [
    customerColumn,
    phoneColumn,
    { key: "segment", label: "Segment" },
    { key: "priority", label: "Priority" },
    daysColumn,
    spendColumn,
    servicesColumn,
  ],
  "high-value": [
    customerColumn,
    phoneColumn,
    lastBillColumn,
    daysColumn,
    spendColumn,
    servicesColumn,
  ],
};

const campaignColumns = [
  { key: "name", label: "Customer" },
  { key: "phone", label: "Phone" },
  { key: "language", label: "Language" },
  { key: "segment", label: "Segment" },
  { key: "priority", label: "Priority" },
  { key: "days_since_last_visit", label: "Days", align: "right" },
  { key: "total_spend", label: "Spend", align: "right", render: (row) => formatMoney(row.total_spend) },
  { key: "suggested_action", label: "Suggested Action" },
  { key: "last_services", label: "Last Services", render: (row) => formatServices(row.last_services) },
];

function MetricCards({ cards }) {
  if (!cards || cards.length === 0) return null;

  return (
    <div className="metric-grid">
      {cards.map((card) => (
        <div className="metric-tile" key={card.label}>
          <div className="label">{card.label}</div>
          <strong>{card.value}</strong>
          {card.detail && (
            <div style={{ color: "var(--ink-700)", fontSize: ".85rem", marginTop: 4 }}>
              {card.detail}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Reports() {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState("bounce");
  const [returnWindow, setReturnWindow] = useState(45);
  const [lostAfter, setLostAfter] = useState(90);
  const [highValueMin, setHighValueMin] = useState(500);
  const [missingAfter, setMissingAfter] = useState(90);
  const [startDate, setStartDate] = useState(currentMonthStart);
  const [endDate, setEndDate] = useState(todayString);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadReport() {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        return_window_days: String(returnWindow || 45),
        lost_after_days: String(lostAfter || 90),
        high_value_min: String(highValueMin || 500),
        missing_after_days: String(missingAfter || 90),
        start_date: startDate,
        end_date: endDate,
      });
      const res = await fetchWithAuth(`/api/reports/marketing/?${params}`);

      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }

      if (!res.ok) {
        setError("Failed to load reports.");
        return;
      }

      setReport(await res.json());
    } catch (err) {
      console.error("report load error", err);
      setError("Error loading reports.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTitle = tabs.find((tab) => tab.id === activeTab)?.label || "Reports";

  const rows = useMemo(() => {
    if (!report) return [];
    if (activeTab === "risk") return report.at_risk_customers || [];
    if (activeTab === "lost") return report.lost_customers || [];
    if (activeTab === "target") return report.bounce_back_target_list || [];
    if (activeTab === "high-value") return report.high_value_missing_customers || [];
    if (activeTab === "campaign") return report.campaign_export || [];
    return report.customers_by_bounce_count || [];
  }, [activeTab, report]);

  const newReturningRows = useMemo(() => {
    if (!report?.new_vs_returning) return [];
    const newRows = (report.new_vs_returning.new_customer_rows || []).map((row) => ({
      ...row,
      type: "New",
    }));
    const returningRows = (report.new_vs_returning.returning_customer_rows || []).map((row) => ({
      ...row,
      type: "Returning",
    }));
    return [...newRows, ...returningRows];
  }, [report]);

  const summaryCards = useMemo(() => {
    if (!report) return [];

    if (activeTab === "bounce") {
      const missedCycleCustomers = (report.customers_by_bounce_count || []).filter(
        (row) => Number(row.bounce_count || 0) > 0
      ).length;
      return [
        { label: "Customers With Bills", value: report.summary?.total_customers_with_bills || 0 },
        { label: "Missed Cycle Customers", value: missedCycleCustomers },
        { label: "Return Window", value: `${returnWindow || 45} days` },
        { label: "Lost After", value: `${lostAfter || 90} days` },
      ];
    }

    if (activeTab === "risk") {
      return [
        { label: "At Risk Customers", value: report.summary?.at_risk_count || 0 },
        { label: "Return Window", value: `${returnWindow || 45} days` },
        { label: "Lost After", value: `${lostAfter || 90} days` },
      ];
    }

    if (activeTab === "lost") {
      return [
        { label: "Lost Customers", value: report.summary?.lost_count || 0 },
        { label: "Lost After", value: `${lostAfter || 90} days` },
      ];
    }

    if (activeTab === "target") {
      return [{ label: "Bounce-Back Targets", value: report.summary?.target_count || 0 }];
    }

    if (activeTab === "monthly") {
      return [
        { label: "Period Customers", value: report.summary?.period_total_customers || 0 },
        { label: "Period Income", value: formatMoney(report.summary?.period_total_income) },
        { label: "Months", value: report.monthly_customer_counts?.length || 0 },
      ];
    }

    if (activeTab === "new-returning") {
      const summary = report.new_vs_returning || {};
      const totalIncome = Number(summary.total_income || 0);
      const newIncomeShare = totalIncome
        ? (Number(summary.new_customer_income || 0) / totalIncome) * 100
        : 0;
      const returningIncomeShare = totalIncome
        ? (Number(summary.returning_customer_income || 0) / totalIncome) * 100
        : 0;
      return [
        { label: "New #", value: summary.new_customers || 0, detail: formatPercent(summary.new_customer_percentage) },
        { label: "Return #", value: summary.returning_customers || 0, detail: formatPercent(summary.returning_customer_percentage) },
        { label: "New Inc", value: formatMoney(summary.new_customer_income), detail: formatPercent(newIncomeShare) },
        { label: "Return Inc", value: formatMoney(summary.returning_customer_income), detail: formatPercent(returningIncomeShare) },
        { label: "Total Inc", value: formatMoney(summary.total_income) },
      ];
    }

    if (activeTab === "frequency") {
      return [
        { label: "Period Customers", value: report.summary?.period_total_customers || 0 },
        { label: "Period Income", value: formatMoney(report.summary?.period_total_income) },
      ];
    }

    if (activeTab === "high-value") {
      return [
        { label: "High-Value Missing", value: report.high_value_missing_customers?.length || 0 },
        { label: "Minimum Spend", value: formatMoney(highValueMin || 500) },
        { label: "Missing After", value: `${missingAfter || 90} days` },
      ];
    }

    if (activeTab === "service-retention") {
      const serviceRows = report.service_retention || [];
      const serviceIncome = serviceRows.reduce((total, row) => total + Number(row.income || 0), 0);
      return [
        { label: "Services Analyzed", value: serviceRows.length },
        { label: "Service Income", value: formatMoney(serviceIncome) },
      ];
    }

    if (activeTab === "campaign") {
      return [{ label: "Campaign Contacts", value: report.campaign_export?.length || 0 }];
    }

    return [];
  }, [activeTab, highValueMin, lostAfter, missingAfter, report, returnWindow]);

  function renderActiveReport() {
    if (activeTab === "monthly") {
      return (
        <Table
          rows={report?.monthly_customer_counts || []}
          columns={[
            { key: "month", label: "Month" },
            { key: "unique_customers", label: "Unique Customers", align: "right" },
            { key: "non_unique_visits", label: "Non-Unique Visits", align: "right" },
            { key: "total_income", label: "Income", align: "right", render: (row) => formatMoney(row.total_income) },
            { key: "average_bill_value", label: "Average Bill", align: "right", render: (row) => formatMoney(row.average_bill_value) },
          ]}
        />
      );
    }

    if (activeTab === "new-returning") {
      return (
        <Table
          rows={newReturningRows}
          columns={[
            { key: "type", label: "Type" },
            { key: "name", label: "Customer" },
            { key: "phone", label: "Phone" },
            { key: "first_bill_date", label: "First Bill" },
            { key: "period_bill_count", label: "Bills", align: "right" },
            { key: "period_income", label: "Income", align: "right", render: (row) => formatMoney(row.period_income) },
          ]}
        />
      );
    }

    if (activeTab === "frequency") {
      return (
        <Table
          rows={report?.customer_frequency || []}
          columns={[
            { key: "bucket", label: "Visit Count" },
            { key: "customers", label: "Customers", align: "right" },
            { key: "income", label: "Income", align: "right", render: (row) => formatMoney(row.income) },
          ]}
        />
      );
    }

    if (activeTab === "service-retention") {
      return (
        <Table
          rows={report?.service_retention || []}
          columns={[
            { key: "service_name", label: "Service" },
            { key: "unique_customers", label: "Customers", align: "right" },
            { key: "retained_customers", label: "Returned Later", align: "right" },
            { key: "retention_rate", label: "Retention", align: "right", render: (row) => formatPercent(row.retention_rate) },
            { key: "income", label: "Income", align: "right", render: (row) => formatMoney(row.income) },
          ]}
        />
      );
    }

    if (activeTab === "campaign") {
      return <Table rows={rows} columns={campaignColumns} emptyText="No campaign contacts found." />;
    }

    return (
      <Table
        rows={rows}
        columns={customerReportColumns[activeTab] || customerReportColumns.bounce}
        emptyText="No customers found."
      />
    );
  }

  const showSegmentFilters = segmentTabs.has(activeTab);
  const showPeriodFilters = periodTabs.has(activeTab);
  const showHighValueFilters = activeTab === "high-value";

  return (
    <div className="card">
      <h3>Reports</h3>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className="btn"
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id ? "var(--pink-600)" : "var(--pink-500)",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "end",
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        {showPeriodFilters && (
          <>
            <label className="label">
              Start Date
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ display: "block", maxWidth: 160, marginTop: 4 }}
              />
            </label>
            <label className="label">
              End Date
              <input
                type="date"
                className="input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ display: "block", maxWidth: 160, marginTop: 4 }}
              />
            </label>
          </>
        )}
        {showSegmentFilters && (
          <>
            <label className="label">
              Return Window
              <input
                type="number"
                min="1"
                className="input"
                value={returnWindow}
                onChange={(e) => setReturnWindow(e.target.value)}
                style={{ display: "block", maxWidth: 120, marginTop: 4 }}
              />
            </label>
            <label className="label">
              Lost After
              <input
                type="number"
                min="1"
                className="input"
                value={lostAfter}
                onChange={(e) => setLostAfter(e.target.value)}
                style={{ display: "block", maxWidth: 120, marginTop: 4 }}
              />
            </label>
          </>
        )}
        {showHighValueFilters && (
          <>
            <label className="label">
              High Value
              <input
                type="number"
                min="0"
                className="input"
                value={highValueMin}
                onChange={(e) => setHighValueMin(e.target.value)}
                style={{ display: "block", maxWidth: 120, marginTop: 4 }}
              />
            </label>
            <label className="label">
              Missing After
              <input
                type="number"
                min="1"
                className="input"
                value={missingAfter}
                onChange={(e) => setMissingAfter(e.target.value)}
                style={{ display: "block", maxWidth: 120, marginTop: 4 }}
              />
            </label>
          </>
        )}
        <button type="button" className="btn" onClick={loadReport} disabled={loading}>
          Refresh
        </button>
      </div>

      {report && <MetricCards cards={summaryCards} />}

      <h4 style={{ marginTop: 0 }}>{activeTitle}</h4>
      {loading && <div>Loading reports...</div>}
      {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}
      {!loading && !error && report && renderActiveReport()}
      {!loading && !error && !report && <div>No report loaded.</div>}
    </div>
  );
}
